import { BareEvent, Event } from '../../shared/types/event';
import { EventTemplate, EventDisplayOption } from '../../shared/types/eventTemplate';
import { VenueTemplate } from '../../shared/types/venueTemplate';
import { Day, Time } from '../../shared/types/time';
import { StaffAllocation, BareStaffAllocation } from '../../shared/types/staffAllocation';
import { timeToMinutes, calculateTimeOffset, DAY_LENGTH } from '../../shared/lib/time';
import { MergedVariables } from '@go-shared/types/mergedVars';
import { mapBy, notUndefined } from '@go-shared/lib/functional';
import { populateEvent } from '@go-shared/lib/event';
import { Mcm } from '@go-shared/types/mcm';
import { Audience } from '@go-shared/types/audience';
import { CallToActionsInstance } from '@go-shared/types/dailyProgram';

const MINIMUM_EVENT_DURATION = 15; // In minutes

function bareStaffAllocation(staffAllocation: StaffAllocation): BareStaffAllocation {
  return {
    ...staffAllocation,
    template: staffAllocation.template._id,
  };
}

// A `PartialEvent` is like an `Event`, but it is not yet built
export interface PartialEvent {
  _id?: string;
  template?: EventTemplate;
  venue?: VenueTemplate;
  startDay?: Day;
  startTime?: Time;
  endTime?: Time;
  staffAllocations: StaffAllocation[];
  hidden?: boolean;
  interactiveFeedDisplay?: EventDisplayOption;
  venueDisplayName?: string;
  deckDisplayName?: string;
  callToAction?: string;
  callToActions?: CallToActionsInstance;
  mergedVars?: MergedVariables;
  imageOverride?: string;
  highlights?: string[];
  mcm?: Mcm;
  audience?: Audience;
}

export function bare(event: Event): BareEvent {
  return {
    ...event,
    staffAllocations: event.staffAllocations.map(bareStaffAllocation),
    day: event.day._id,
    template: event.template._id,
    venue: event.venue._id,
    highlights: (event.highlights || []).map(a => a._id),
  };
}

/**
 * Rebound function so we don't need to refactor all the imports
 * Since now the server uses the populate function, we moved it to shared library
 *
 */

export const populate = populateEvent;

/**
 * Takes a partial event, as entered by a user in the `EventForm`, then
 * validates it and returns an Event that can be sent to the server for
 * update or creation
 *
 */
export function partialEventToEvent(partial: PartialEvent, dayStartMinutes: number): Event {
  if (!partial.template) throw new Error(`Event not assigned`);
  if (!partial.venue) throw new Error(`Event venue not assigned`);
  if (!partial.startDay) throw new Error(`Event start day not assigned`);
  if (!partial.startTime) throw new Error(`Event start time not assigned`);
  if (!partial.endTime) throw new Error(`Event end time not assigned`);

  let startTimeMinutes = timeToMinutes(partial.startTime, dayStartMinutes);
  let endTimeMinutes = timeToMinutes(partial.endTime, dayStartMinutes);
  let duration = endTimeMinutes - startTimeMinutes;

  if (endTimeMinutes === 0) {
    endTimeMinutes = DAY_LENGTH;
  }

  if (endTimeMinutes <= startTimeMinutes) {
    throw new Error(`Event end time must be after start time`);
  }

  if (duration < MINIMUM_EVENT_DURATION) {
    throw new Error(`Event duration must be at least 15 minutes`);
  }

  const highlightsMap = mapBy(partial.template.brand.eventHighlights || [], '_id');

  const event: Event = {
    _id: partial._id || '',
    template: partial.template,
    venue: partial.venue,
    day: partial.startDay,
    startTime: partial.startTime,
    duration,
    staffAllocations: partial.staffAllocations,
    hidden: !!partial.hidden,
    interactiveFeedDisplay: partial.interactiveFeedDisplay || undefined,
    venueDisplayName: partial.venueDisplayName,
    deckDisplayName: partial.deckDisplayName,
    callToActions: partial.callToActions || { syncWithTemplate: false, values: [] },
    mergedVars: partial.mergedVars,
    imageOverride: partial.imageOverride,
    highlights: (partial.highlights || []).map(id => highlightsMap[id]).filter(notUndefined),
    mcm: partial.mcm,
  };

  return event;
}

export function eventToPartialEvent(event: Event, dayStart: Time): PartialEvent {
  let partialEvent: PartialEvent = {
    ...event,
    highlights: (event.highlights || []).map(a => a._id),
  };

  let timeOffset = calculateTimeOffset(event.startTime, event.duration, timeToMinutes(dayStart));

  partialEvent.endTime = timeOffset.time;

  return partialEvent;
}
